import { useEffect, useState, useRef } from 'react'
import Header from './Header.jsx'
import MainVisual from './MainVisual.jsx'
import FronPageMenu from './FrontPageMenu.jsx'
import CatchPhrase from './CatchPhrase.jsx'
import Numbers from './Numbers.jsx'
import VideoAndParagraph from './VideoAndParagraph.jsx'
import AboutDemolitionWork from './AboutDemolitionWork.jsx'
import ConstructionExample from './ConstructionExample.jsx'
import FrontPageRecruit from './FrontPageRecruit.jsx'
import FrontPageBottomCatch from './FrontPageBottomCatch.jsx'
import News from './News.jsx'
import Footer from './PageFooter_temporary.jsx'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import apiFetch from '@wordpress/api-fetch'

import '@mantine/core/styles.css'

import getImageUrl from './get-image-url.js'

import { useMediaQuery } from 'react-responsive'
import { motion, AnimatePresence } from 'framer-motion'

function FrontPage() {
  const isMD = useMediaQuery({ query: '(min-width: 768px)' })
  const isLG = useMediaQuery({ query: '(min-width: 1024px)' })
  const [banners, setBanners] = useState([])
  const bannersRef = useRef(null)
  const [isCarousel, setIsCarousel] = useState(false)

  useEffect(() => {
    apiFetch({
      path: `${import.meta.env.VITE_SITE_URL}/wp-json/myroutes/banners`,
    }).then((data) => {
      setBanners(data)
    })
  }, [])

  useEffect(() => {
    if (bannersRef.current && banners.length) {
      bannersRef.current.splide.Components.Autoplay.play()
    }
  }, [bannersRef, banners])

  useEffect(() => {
    const bannerAreaWrapper = document.getElementById('banner-area-wrapper')
    const bannerWidth = bannerAreaWrapper?.offsetWidth
    const gap = 30 // Splideのgapと同じ値を設定

    // すべての画像が読み込まれるのを待つPromiseを作成
    const loadImages = banners.map((banner) => {
      return new Promise((resolve) => {
        const img = new Image()
        img.onload = () => resolve(img.width) // 画像が読み込まれたら幅を返す
        img.src = banner.crb_banner
      })
    })

    // すべての画像が読み込まれたら実行
    Promise.all(loadImages).then((widths) => {
      const totalBannerWidth = widths.reduce((total, width) => {
        if (width >= 300) {
          return total + 300 + gap
        } else {
          return total + width + gap
        }
      }, 0)

      if (totalBannerWidth > bannerWidth) {
        setIsCarousel(true)
      }
    })

    window.addEventListener('resize', () => {
      const bannerWidth = bannerAreaWrapper?.offsetWidth
      Promise.all(loadImages).then((widths) => {
        const totalBannerWidth = widths.reduce((total, width) => {
          if (width >= 300) {
            return total + 300 + gap
          } else {
            return total + width + gap
          }
        }, 0)

        if (totalBannerWidth > bannerWidth) {
          setIsCarousel(true)
        } else {
          setIsCarousel(false)
        }
      })
    })
  }, [bannersRef, banners])

  return (
    <>
      <Header />
      <MainVisual />
      <FronPageMenu />
      <main className={isMD && 'mt-[-53px]'}>
        <div className="front-page-main-wrapper">
          <CatchPhrase />
          <Numbers />
          <VideoAndParagraph />
        </div>
        <AboutDemolitionWork />
        <ConstructionExample />
        <News />
        <FrontPageRecruit />
        <AnimatePresence>
          {isMD ? (
            <>
              <div
                id="banner-area-wrapper"
                className="py-[120px] px-3 flex justify-center"
              >
                <Splide
                  aria-label="バナー"
                  options={{
                    pagination: false,
                    gap: 30,
                    arrows: false,
                    interval: 2000,
                    autoWidth: true,
                    autoplay: true,
                    type: isCarousel ? 'loop' : 'slide', // isCarouselの値に基づいてtypeを設定
                  }}
                  ref={bannersRef}
                >
                  {banners ? (
                    banners.map((item, index) => (
                      <SplideSlide
                        className="flex items-center"
                        key={index}
                      >
                        <a
                          key={index}
                          href={item.crb_banner_url}
                          className="block max-w-[300px] overflow-hidden h-fit"
                        >
                          <motion.img
                            src={item.crb_banner}
                            initial={{
                              scale: 1,
                            }}
                            whileHover={{
                              scale: 1.1,
                            }}
                            transition={{
                              ease: [0.165, 0.84, 0.44, 1],
                              duration: 0.6,
                            }}
                          />
                        </a>
                      </SplideSlide>
                    ))
                  ) : (
                    <SplideSlide></SplideSlide>
                  )}
                </Splide>
              </div>
            </>
          ) : (
            <div className="py-[calc(152vw/7.5)]">
              <Splide
                aria-label="バナー"
                options={{
                  pagination: false,
                  gap: 20,
                  arrows: false,
                  trimSpace: false,
                  interval: 2000,
                  focus: 'center',
                  autoWidth: true,
                  autoplay: true,
                  type: 'loop',
                }}
                ref={bannersRef}
              >
                {banners ? (
                  banners.map((item, index) => (
                    <SplideSlide
                      key={index}
                      className="max-w-[70%] flex items-center"
                    >
                      <a
                        href={item.crb_banner_url}
                        className="w-fit block"
                      >
                        <img src={item.crb_banner} />
                      </a>
                    </SplideSlide>
                  ))
                ) : (
                  <SplideSlide></SplideSlide>
                )}
              </Splide>
            </div>
          )}
        </AnimatePresence>
        <FrontPageBottomCatch />
      </main>
      <Footer />
      {isLG ? (
        <style jsx>{`
          .front-page-main-wrapper {
            background-image: url(${getImageUrl('main-bg-top.webp')}),
              linear-gradient(180deg, #2095d2 0%, #1c9ed7 100%),
              url(${getImageUrl('main-bg-bottom.webp')});
            background-repeat: no-repeat;
            background-position: top center, center 1298px, bottom center;
            background-size: 1920px 1300px, 1920px calc(100% - 1300px - 800px + 4px), 1920px 800px;
          }
        `}</style>
      ) : (
        <style jsx>{`
          .front-page-main-wrapper {
            background-image: url(${getImageUrl('main-bg-top.webp')}),
              linear-gradient(180deg, #2095d2 0%, #1c9ed7 100%),
              url(${getImageUrl('main-bg-bottom.webp')});
            background-repeat: no-repeat;
            background-position: top center, center 678px, bottom center;
            background-size: auto 680px, 1920px calc(100% - 680px - 400px + 4px), auto 400px;
          }
          @media (min-width: 768px) {
            .front-page-main-wrapper {
              background-size: auto 680px, 1920px calc(100% - 680px - 500px + 4px), auto 500px;
            }
          }
        `}</style>
      )}
    </>
  )
}

export default FrontPage
